import React from 'react';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import BoardHeaderActions from '../components/BoardHeaderActions';
import { useBoardFeature } from '../sharedBoard';
import { Alert, Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import FilterListRounded from '@mui/icons-material/FilterListRounded';
import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import { useWideBoard } from '../../DashboardRoute';
import {
  downloadLookupExport, fetchLookupDashboard, fetchLookupFilterOptions, fetchLookupInputOptions,
  type LookupDateRange,
} from '../../../services/manualDashboardService';
import type { LookupBlock, LookupDashboardPayload, LookupFilterOptionsPayload } from '../../../types/lookupDashboard';
import {
  DATE_PRESETS,
  DateRangePicker,
  EmptyState,
  FilterBar,
  MetricCard,
  PageHeader,
  PageToolbar,
  SegmentedControl,
  SelectMenu,
  SidePanelDock,
  ToolbarButton,
  useCollapseOnScroll,
  useDebouncedCommit,
  useElementWidth,
  lastFullMonths,
  type DateRangePreset,
  type FreshnessStatus,
} from '../../../ui-kit';
import { createSurfaceChatStore } from '../../surface-chat/createSurfaceChatStore';
import { useSurfaceChat } from '../../surface-chat/useSurfaceChat';
import BoardChatPanel from '../components/BoardChatPanel';
import { useBoardZone } from '../useBoardZone';
import { formatValue } from '../format';
import LookupInputField from './components/LookupInputField';
import GridPanel from './components/GridPanel';
import MonthlyPanel from './components/MonthlyPanel';
import RankingPanel from './components/RankingPanel';
import RecordsPanel from './components/RecordsPanel';
import RulesPanel from './components/RulesPanel';
import CallsBreakdown from './components/CallsBreakdown';
import { buildLookupSurfaceContext, lookupChatFor } from './lookupChat';
import { countLabel, measureFormat } from './lookupFormat';

// One chat per mounted board, kept outside the component so the conversation
// survives re-renders and a new lookup.
const chatStores = new Map<string, ReturnType<typeof createSurfaceChatStore>>();
function chatStoreFor(dashboardId: string, name: string) {
  let store = chatStores.get(dashboardId);
  if (!store) {
    store = createSurfaceChatStore(name);
    chatStores.set(dashboardId, store);
  }
  return store;
}

type Inputs = Record<string, string>;

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** The viewer's own weekday, from this browser's clock: "Mon". */
const viewersWeekday = () => WEEKDAYS[(dayjs().day() + 6) % 7];

/**
 * A lookup board: type the inputs, see everything on record for them.
 *
 * It opens on its default view, the most-flown route, every time — reloads
 * included. What is picked but not yet complete is a draft.
 */
export default function LookupDashboardPage() {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const navigate = useNavigate();
  const feature = useBoardFeature();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // What was looked up. Held here, not in the address: opening or reloading
  // the board always starts from its default view — the most-flown route —
  // rather than bringing back a pair that may have come back empty.
  const [applied, setApplied] = React.useState<Inputs>({});

  // An address from before, carrying inputs, is tidied so it cannot linger.
  React.useEffect(() => {
    // The route's state rides along, so the page is not asked again which kind it is.
    if (searchParams.toString()) setSearchParams(new URLSearchParams(), { replace: true, state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [payload, setPayload] = React.useState<LookupDashboardPayload | null>(null);
  const [draft, setDraft] = React.useState<Inputs>(applied);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  // Which table's download is being prepared, and any that failed, by block id.
  const [downloading, setDownloading] = React.useState<string | null>(null);
  const [downloadProblem, setDownloadProblem] = React.useState<{ block: string; message: string } | null>(null);
  const [boardRef, boardWidth] = useElementWidth<HTMLDivElement>();
  const [chatOpen, setChatOpen] = React.useState(false);

  // The picked date range on boards that have one; null is all time.
  const [range, setRange] = React.useState<LookupDateRange | null>(null);
  const rangeKey = JSON.stringify(range);

  // The weekday, on boards with a day picker: opened on the viewer's own
  // today, so each case manager starts on their day. Boards without one ignore it.
  const [day, setDay] = React.useState<string>(viewersWeekday);

  // The time zone the board's times read in, on boards with a zone picker
  // (Kim, 20 Oct 2026). Boards without one ignore it.
  const [zone, pickZone] = useBoardZone(dashboardId);

  // Optional filters — broker company, client — narrowing every figure.
  const [filters, setFilters] = React.useState<Record<string, string[]>>({});
  const [filterOptions, setFilterOptions] = React.useState<LookupFilterOptionsPayload['filters']>([]);
  const [filterOptionsLoading, setFilterOptionsLoading] = React.useState(true);
  // The row of a grid that opens the list (an hour of Planned calls by hour)
  // picked to narrow it, and the list, to bring into view.
  const [openedRow, setOpenedRow] = React.useState<string | null>(null);
  const recordsRef = React.useRef<HTMLDivElement | null>(null);
  const filtersKey = JSON.stringify(filters);

  useWideBoard(true, chatOpen);

  React.useEffect(() => {
    if (!dashboardId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchLookupDashboard(
      dashboardId, { inputs: applied, filters, refresh: refreshNonce > 0, range: range ?? undefined, day, zone }, controller.signal,
    )
      .then(setPayload)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, applied, filtersKey, rangeKey, refreshNonce, day, zone]);

  // The route on screen — the most-flown one included — as the server
  // resolved it. The filter lists only offer values on it.
  const shownRoute = React.useMemo(() => {
    const out: Inputs = {};
    payload?.inputs.forEach((i) => {
      if (i.value) out[i.key] = i.value;
    });
    return out;
  }, [payload]);
  const shownRouteKey = JSON.stringify(shownRoute);

  // Filter values follow the route and what is picked: broker companies that
  // flew this route, and a client list narrowed to the broker companies
  // chosen. A client picked earlier that is not among them any more is
  // dropped, so the board never filters to a pair with nothing on record.
  React.useEffect(() => {
    if (!dashboardId || !payload) return undefined;
    const controller = new AbortController();
    setFilterOptionsLoading(true);
    fetchLookupFilterOptions(
      dashboardId, filters, JSON.parse(shownRouteKey) as Inputs, controller.signal, range ?? undefined,
    )
      .then((data) => {
        setFilterOptions(data.filters);
        setFilters((current) => {
          let changed = false;
          const next = { ...current };
          data.filters.forEach((f) => {
            if (!f.narrowed_by.length || !next[f.key]?.length) return;
            const allowed = new Set(f.options.map((o) => o.value));
            const kept = next[f.key].filter((v) => allowed.has(v));
            if (kept.length !== next[f.key].length) {
              next[f.key] = kept;
              changed = true;
            }
          });
          return changed ? next : current;
        });
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setFilterOptionsLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, filtersKey, shownRouteKey, rangeKey, refreshNonce, payload === null]);

  const [filterDraft, setFilterDraft] = useDebouncedCommit(filters, setFilters, 300);
  const toolbarRef = React.useRef<HTMLElement | null>(null);
  const filterPanel = useCollapseOnScroll(toolbarRef, { resetKey: chatOpen });
  const filtersId = React.useId();
  const appliedFilterCount = Object.values(filters).filter((v) => v.length > 0).length;

  const inputDefs = React.useMemo(() => payload?.inputs ?? [], [payload]);

  // Opened on the default route, the fields show it, so it can be swapped
  // or edited rather than typed from scratch.
  // With nothing looked up yet, a filter change moves the default route too
  // (a broker company's most-flown route), so the fields follow it.
  React.useEffect(() => {
    if (!payload?.defaulted || Object.keys(applied).length) return;
    const shown: Inputs = {};
    payload.inputs.forEach((i) => {
      if (i.value) shown[i.key] = i.value;
    });
    setDraft(shown);
  }, [payload, applied]);
  const boardName = payload?.spec.name ?? 'Dashboard';

  // Looked up once every input has a value; until then the pick is held.
  const lookUp = React.useCallback((next: Inputs) => {
    if (inputDefs.some((i) => !next[i.key]?.trim())) return;
    const chosen: Inputs = {};
    inputDefs.forEach((i) => {
      chosen[i.key] = next[i.key].trim();
    });
    // A new route starts unfiltered: its broker companies and clients are
    // different, so the reader picks again from the ones on it.
    if (JSON.stringify(chosen) !== shownRouteKey) {
      setFilters((current) => (Object.keys(current).length ? {} : current));
    }
    setApplied(chosen);
  }, [inputDefs, shownRouteKey]);

  // Which input's list is open, when the page opened it.
  const [openInput, setOpenInput] = React.useState<string | null>(null);
  // Only the latest pick may fill in what follows it.
  const pickSeq = React.useRef(0);

  /**
   * Inputs are chosen in order: the origin on its own, then the destination
   * among the airports flown from it.
   *
   * Picking the last input looks it up. Picking an earlier one clears what
   * follows, fills in the most-flown next value with it — so the board moves
   * straight to a real route rather than sitting empty — and opens that next
   * list, so the suggestion can be accepted or changed in one click. With
   * nothing on record to suggest, the next input stays blank and open.
   */
  const pick = async (key: string, value: string) => {
    const index = inputDefs.findIndex((i) => i.key === key);
    if (draft[key] === value) return;
    const seq = ++pickSeq.current;
    const next: Inputs = { ...draft, [key]: value };
    const following = inputDefs.slice(index + 1);
    if (!following.length) {
      setDraft(next);
      lookUp(next);
      return;
    }
    following.forEach((i) => {
      delete next[i.key];
    });
    setDraft(next);

    const [nextInput] = following;
    if (dashboardId && following.length === 1) {
      try {
        const data = await fetchLookupInputOptions(dashboardId, nextInput.key, { inputs: next });
        const top = data.options.find((o) => o.pair_count > 0);
        if (seq !== pickSeq.current) return;
        if (top) {
          const filled = { ...next, [nextInput.key]: top.value };
          setDraft(filled);
          lookUp(filled);
        }
      } catch {
        // No suggestion; the next input is left for the reader to choose.
      }
    }
    if (seq === pickSeq.current) setOpenInput(nextInput.key);
  };

  /** The inputs before this one, which rank its suggestions. */
  const earlierInputs = (key: string): Inputs => {
    const out: Inputs = {};
    for (const i of inputDefs) {
      if (i.key === key) break;
      if (draft[i.key]) out[i.key] = draft[i.key];
    }
    return out;
  };

  // Only a two-input board has a direction to reverse.
  const swap = inputDefs.length === 2 ? () => {
    const [a, b] = inputDefs;
    const next = { ...draft, [a.key]: draft[b.key] ?? '', [b.key]: draft[a.key] ?? '' };
    setDraft(next);
    lookUp(next);
  } : null;

  const chatStore = chatStoreFor(dashboardId ?? 'unknown', boardName);
  const surfaceContextRef = React.useRef<Record<string, unknown> | null>(null);
  const getSurfaceContext = React.useCallback(() => surfaceContextRef.current, []);
  // The conversation is found from the database, not remembered in this
  // browser: the same board opens the same chat on any machine.
  const { sendMessage, sendSQLApproval, stopStream } = useSurfaceChat({
    useStore: chatStore,
    dashboardId: dashboardId ?? '',
    getSurfaceContext,
    enabled: chatOpen,
  });

  // Downloads what is on screen — the default route included — so the file
  // always matches the board.
  const download = async (block: string, which?: string, segment?: string, image?: 'png' | 'svg') => {
    if (!dashboardId || !payload) return;
    const shown: Inputs = {};
    payload.inputs.forEach((i) => {
      if (i.value) shown[i.key] = i.value;
    });
    setDownloading(block);
    setDownloadProblem(null);
    try {
      await downloadLookupExport(dashboardId, shown, which, filters, range ?? undefined, {
        day: payload.day_picker?.value, zone: payload.zone_picker?.value, segment, image,
      });
    } catch (err) {
      setDownloadProblem({ block, message: `Could not download: ${(err as Error).message}` });
    } finally {
      setDownloading(null);
    }
  };

  if (feature.loading) {
    return <Box sx={{ px: 0.5 }}><Skeleton variant="rounded" height={320} /></Box>;
  }
  if (!feature.enabled) {
    return (
      <Box sx={{ px: 0.5 }}>
        <Alert severity="info">
          Manual dashboards are not enabled for this workspace. Ask an admin to turn on the
          &ldquo;Manual Dashboards&rdquo; feature.
        </Alert>
      </Box>
    );
  }
  if (error && !payload) {
    return (
      <Box sx={{ px: 0.5 }}>
        <Alert severity="error" onClose={() => navigate('/dashboard')}>{error}</Alert>
      </Box>
    );
  }
  if (!payload) {
    return (
      <Stack spacing={1.5} sx={{ px: 0.5 }}>
        <Skeleton variant="rounded" height={88} />
        <Skeleton variant="rounded" height={104} />
        <Skeleton variant="rounded" height={300} />
      </Stack>
    );
  }

  surfaceContextRef.current = buildLookupSurfaceContext(dashboardId ?? '', payload);
  const chatPlace = lookupChatFor(payload);

  // The date range: All time, the board's opening view, spans the first to
  // the last record on file; the other shortcuts count back from today.
  const dates = payload.date_filter ?? null;
  const allTime: DateRangePreset | null = dates?.first && dates.last
    ? {
        id: 'all_time',
        label: 'All time',
        resolve: () => ({ start: dayjs(dates.first as string), end: dayjs(dates.last as string) }),
      }
    : null;
  const datePresets: DateRangePreset[] = [
    ...(allTime ? [allTime] : []),
    DATE_PRESETS.last90Days,
    lastFullMonths(6),
    DATE_PRESETS.last12MonthsToDate,
    DATE_PRESETS.yearToDate,
    DATE_PRESETS.lastYear,
  ];
  const datePicker = dates && dates.first && dates.last ? (
    <DateRangePicker
      key="dates"
      value={{ start: range?.start ?? dates.first, end: range?.end ?? dates.last }}
      onChange={(next, preset) => setRange(preset?.id === 'all_time' ? null : { start: next.start, end: next.end })}
      presets={datePresets}
      minDate={dates.first}
      maxDate={dates.last}
      today={dates.today ?? undefined}
      showPresetLabel
      ariaLabel={dates.label}
    />
  ) : null;

  const noun = payload.spec.record_noun;
  const find = <T extends LookupBlock['type']>(type: T) =>
    payload.blocks.filter((b) => b.type === type) as Extract<LookupBlock, { type: T }>[];
  const [summary] = find('summary');
  const rankings = find('ranking');
  const [monthly] = find('monthly');
  const [records] = find('records');
  const grids = find('grid');
  const recordsFirst = !!records && payload.blocks.indexOf(records) < payload.blocks.findIndex((b) => b.type === 'ranking');
  // Grids the spec lists after the rankings, when the list comes first, sit
  // at the foot of the page (the Daily Call Plan's past pickups, under By
  // case manager); other grids after the list straight after it; the rest above.
  const lastRanking = payload.blocks.reduce((at, b, i) => (b.type === 'ranking' ? i : at), -1);
  const gridsLast = recordsFirst && lastRanking >= 0
    ? grids.filter((g) => payload.blocks.indexOf(g) > lastRanking) : [];
  const gridsAfter = records
    ? grids.filter((g) => payload.blocks.indexOf(g) > payload.blocks.indexOf(records) && !gridsLast.includes(g)) : [];
  const gridsBefore = grids.filter((g) => !gridsAfter.includes(g) && !gridsLast.includes(g));
  // A grid whose rows open the list (an hour of Planned calls by hour) sets
  // the list's menu to that row and brings the list into view.
  const opener = records ? grids.find((g) => g.opens?.block === records.id) : undefined;
  const openRow = (key: string) => {
    setOpenedRow(key);
    const list = recordsRef.current;
    if (!list) return;
    // Stop below the toolbar, which stays pinned over the top of the page.
    list.style.scrollMarginTop = `${(toolbarRef.current?.offsetHeight ?? 0) + 16}px`;
    list.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const gridPanel = (g: (typeof grids)[number]) => (
    <GridPanel
      key={g.id}
      block={g}
      loading={loading}
      onDownload={(format) => download(g.id, g.id, undefined, format === 'xlsx' ? undefined : format)}
      downloading={downloading === g.id}
      problem={downloadProblem?.block === g.id ? downloadProblem.message : null}
      onRowOpen={g === opener ? openRow : undefined}
    />
  );
  // The menu offers the grid's rows, each with its total; a pick the rows no
  // longer hold (another day, zone or filter) reads as none.
  const narrowOptions = opener
    ? opener.rows.map((r, i) => ({ value: r.key, label: r.label, count: opener.totals?.rows[i] ?? 0 }))
    : [];
  const narrowValue = narrowOptions.some((o) => o.value === openedRow) ? openedRow : null;
  const recordsPanel = (block: typeof records) => (
    <Box ref={recordsRef}>
    <RecordsPanel
      block={block}
      loading={loading}
      footer={recordsFooter}
      onDownload={(segment) => download(block.id, undefined, segment)}
      downloading={downloading === block.id}
      problem={downloadProblem?.block === block.id ? downloadProblem.message : null}
      renderDetail={(row, key, close) => (
        <CallsBreakdown row={row} logKey={key} onClose={close} viewerZone={payload.zone_picker?.value ?? 'ET'} />
      )}
      narrow={opener?.opens ? {
        label: opener.opens.label,
        column: opener.opens.column,
        value: narrowValue,
        options: narrowOptions,
        onChange: setOpenedRow,
      } : undefined}
    />
    </Box>
  );
  const lookedUp = payload.inputs.map((i) => i.value).filter(Boolean).join(' → ');
  const recordCount = payload.meta.record_count ?? payload.meta.matched_rows;
  // A route that is empty only because of the filters says which filters, and
  // what the route has without them, so it never reads as "nobody flies this".
  const pickedFilters = (payload.filters ?? []).filter((f) => f.selected.length > 0);
  const namesOf = (values: string[]) =>
    values.length > 2 ? `${values[0]} or ${values.length - 1} others` : values.join(' or ');
  const unfiltered = payload.meta.unfiltered_rows ?? 0;
  // `unfiltered` counts the inputs with no filters and no dates at all, so
  // zero there really means nothing on record.
  const filterText = pickedFilters.map((f) => `${f.label.toLowerCase()} ${namesOf(f.selected)}`).join(' and ');
  const dateText = range && dates
    ? `with a ${dates.label.toLowerCase()} from ${range.start ?? 'the first record'} to ${range.end ?? 'the latest'}`
    : '';
  const narrowedBy = filterText && dateText ? 'these filters and dates' : dateText ? 'these dates' : 'these filters';
  const emptyForFilters = {
    title: [`No ${noun}s on ${lookedUp}`, filterText && `for ${filterText}`, dateText].filter(Boolean).join(' '),
    message:
      unfiltered > 0
        ? `${lookedUp} has ${countLabel(unfiltered, noun)} in total, but none match ${narrowedBy}.`
        : `${lookedUp} has no ${noun}s on record at all. ${payload.spec.empty_note ?? 'Try the other direction or another airport.'}`,
  };

  const freshness: FreshnessStatus = payload.meta.refreshing
    ? 'updating'
    : payload.meta.status === 'failed' ? 'failed' : payload.meta.stale ? 'stale' : 'fresh';

  const recordsFooter = (
    <Box component="span" sx={{ display: 'block' }}>
      Data as of {payload.meta.as_of}
      {payload.spec.source_label ? ` from ${payload.spec.source_label}` : ''}.
      {payload.spec.scope_note ? ` ${payload.spec.scope_note}.` : ''}
    </Box>
  );

  // A ranking table reads comfortably from about 460px; below that its six
  // columns start to scroll.
  const rankingColumns = Math.max(1, Math.min(3, Math.floor((boardWidth + 12) / 472)));

  // Ranking tables side by side, as many as the board's own width holds
  // without squeezing their columns — so opening chat drops a column rather
  // than cramping all of them. Panels in a row share its height, and a table
  // left alone on a row takes all of it.
  const renderRankings = (blocks: typeof rankings) =>
    blocks.length > 0 ? (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${rankingColumns}, minmax(0, 1fr))`,
          gap: 1.5,
          alignItems: 'stretch',
        }}
      >
        {blocks.map((block, i) => (
          <Box
            key={block.id}
            sx={{
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              '& > *': { flex: 1 },
              ...(i === blocks.length - 1 && blocks.length % rankingColumns !== 0
                ? { gridColumn: `span ${rankingColumns - (blocks.length % rankingColumns) + 1}` }
                : {}),
            }}
          >
            <RankingPanel
              block={block}
              loading={loading}
              picked={block.filter ? filters[block.filter] ?? [] : []}
              onPick={block.filter ? (label) => {
                const key = block.filter as string;
                // Picking the name already picked clears it.
                setFilters((current) => ({
                  ...current,
                  [key]: current[key]?.length === 1 && current[key][0] === label ? [] : [label],
                }));
              } : undefined}
              onDownload={() => download(block.id, block.id)}
              downloading={downloading === block.id}
              problem={downloadProblem?.block === block.id ? downloadProblem.message : null}
            />
          </Box>
        ))}
      </Box>
    ) : null;

  const board = (
    <Box ref={boardRef} sx={{ px: 0.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minWidth: 0 }}>
      {/* Named like every dashboard, with how current it is and Chat at the
          top right. Kept to two lines: the source and rules are said under
          the records table, where they explain what is in it. */}
      <PageHeader
        ariaLabel="About this dashboard"
        title={payload.spec.name}
        description={payload.spec.description ?? undefined}
        actions={
          <BoardHeaderActions
            dashboardId={dashboardId ?? ''}
            name={payload.spec.name}
            updatedAt={payload.meta.built_at}
            status={freshness}
            refreshing={refreshing}
            onRefresh={() => { setRefreshing(true); setRefreshNonce((n) => n + 1); }}
            loading={loading}
            chatOpen={chatOpen}
            onToggleChat={() => setChatOpen((v) => !v)}
            chatTooltip="Ask about this dashboard"
          />
        }
      />

      {/* The inputs, kept in view while the results scroll. Picking a value
          looks it up, the way the cohort board's filters apply as picked. */}
      <PageToolbar
        ref={toolbarRef}
        ariaLabel={payload.inputs.length
          ? `Choose ${payload.inputs.map((i) => i.label.toLowerCase()).join(' and ')}`
          : `Choose ${[payload.day_picker?.label, payload.filters.length ? 'filters' : null].filter(Boolean).join(' and ').toLowerCase() || 'what to show'}`}
        loading={loading}
        secondaryOpen={filterPanel.shown}
        secondaryId={filtersId}
        start={[...payload.inputs.map((input, i) => (
          <React.Fragment key={input.key}>
            {i === 1 && swap && (
              <ToolbarButton
                variant="ghost"
                icon={<SwapHorizRounded />}
                onClick={swap}
                aria-label={`Swap ${payload.inputs[0].label.toLowerCase()} and ${input.label.toLowerCase()}`}
                tooltip="Swap direction"
              />
            )}
            <LookupInputField
              dashboardId={dashboardId}
              input={input}
              value={draft[input.key] ?? ''}
              valueName={draft[input.key] === input.value ? input.value_label : null}
              onChange={(value) => { void pick(input.key, value); }}
              otherInputs={earlierInputs(input.key)}
              open={openInput === input.key}
              onOpenChange={(isOpen) => setOpenInput(isOpen ? input.key : null)}
              recordNoun={noun}
            />
          </React.Fragment>
        )),
        // The range, the time zone, then the day: which cases count, how
        // times read, then which day to plan for.
        datePicker && (
          <React.Fragment key="dates">
            {payload.inputs.length > 0 && <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />}
            {datePicker}
          </React.Fragment>
        ),
        // Which zone the suggested times read in, so nobody converts in their
        // head. Full names, since people know them as EST and CST as often as
        // ET and CT; picked once and remembered, so a menu rather than buttons.
        payload.zone_picker && (
          <React.Fragment key="zone">
            {(payload.inputs.length > 0 || datePicker) && <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />}
            <SelectMenu
              label={payload.zone_picker.label}
              value={payload.zone_picker.value}
              options={payload.zone_picker.options.map((o) => ({ value: o.key, label: `${o.label} (${o.key})` }))}
              onChange={pickZone}
              searchable={false}
            />
          </React.Fragment>
        ),
        payload.day_picker && (
          <React.Fragment key="day">
            {(payload.inputs.length > 0 || datePicker || payload.zone_picker) && <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />}
            <SegmentedControl
              value={payload.day_picker.value}
              options={payload.day_picker.options.map((d) => ({
                value: d,
                // The viewer's own today is named, so another day reads as picked.
                label: d === viewersWeekday() ? `${d} · today` : d,
              }))}
              onChange={setDay}
              ariaLabel={payload.day_picker.label}
            />
          </React.Fragment>
        ),
        payload.filters.length > 0 && (
          <React.Fragment key="filters">
            <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
            <ToolbarButton
              icon={<FilterListRounded />}
              onClick={filterPanel.toggle}
              selected={filterPanel.shown}
              count={appliedFilterCount}
              aria-expanded={filterPanel.shown}
              aria-controls={filtersId}
              tooltip={filterPanel.shown ? 'Hide filters' : 'Show filters'}
            >
              Filters
            </ToolbarButton>
          </React.Fragment>
        )]}
        end={(() => {
          // Mid-choice, say what is still needed; the board still shows the last route.
          const missing = payload.inputs.find((i) => !draft[i.key]);
          const note = missing
            ? `Choose ${/^[aeiou]/i.test(missing.label) ? 'an' : 'a'} ${missing.label.toLowerCase()}`
            : payload.defaulted && !Object.keys(applied).length ? payload.spec.default_note : null;
          return note ? (
            <Typography
              variant="caption"
              sx={(t) => ({ color: missing ? t.tokens.text.secondary : t.tokens.text.tertiary })}
            >
              {note}
            </Typography>
          ) : undefined;
        })()}
      >
        {payload.filters.length > 0 && (
          <FilterBar
            fields={filterOptions.map((f) => ({ key: f.key, label: f.label, options: f.options }))}
            value={filterDraft}
            onChange={setFilterDraft}
            loading={filterOptionsLoading}
          />
        )}
      </PageToolbar>

      {payload.meta.status === 'failed' && payload.meta.error && (
        <Alert severity="warning">
          Could not refresh from the warehouse, so these are the records from{' '}
          {payload.meta.built_at ? new Date(payload.meta.built_at).toLocaleString() : 'the last successful build'}.
          <Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled' }}>
            {payload.meta.error}
          </Typography>
        </Alert>
      )}
      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

      {!payload.ready ? (
        <EmptyState
          height={240}
          message={
            payload.spec.description
              ? `${payload.spec.description} Enter ${payload.inputs.map((i) => i.label).join(' and ')} to begin.`
              : `Enter ${payload.inputs.map((i) => i.label).join(' and ')} to begin.`
          }
        />
      ) : payload.meta.matched_rows === 0 && !loading ? (
        pickedFilters.length > 0 || range ? (
          <EmptyState
            height={240}
            title={emptyForFilters.title}
            message={emptyForFilters.message}
            action={
              <ToolbarButton
                icon={<RestartAltRounded fontSize="small" />}
                onClick={() => { setFilters({}); setRange(null); }}
              >
                {range && !pickedFilters.length ? 'Show all dates' : 'Clear filters'}
              </ToolbarButton>
            }
          />
        ) : (
          <EmptyState
            height={240}
            message={`No ${noun}s on record for ${lookedUp}. ${payload.spec.empty_note ?? 'Try the other direction, or ask chat which operators fly nearby airports.'}`}
          />
        )
      ) : (
        <>
          {summary && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(1, minmax(0, 1fr))',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: `repeat(${summary.tiles.length}, minmax(0, 1fr))`,
                },
                gap: 1.5,
              }}
            >
              {summary.tiles.map((tile) => (
                <MetricCard
                  key={tile.key}
                  label={tile.label}
                  value={formatValue(tile.value, measureFormat(tile.format))}
                  info={tile.definition ?? undefined}
                  note={tile.note ?? (tile.secondary ? undefined : ' ')}
                  unit={tile.unit ?? undefined}
                  secondary={tile.secondary ? {
                    value: formatValue(tile.secondary.value, measureFormat(tile.secondary.format)),
                    label: tile.secondary.label,
                  } : undefined}
                  loading={loading}
                  comparison={tile.comparison && tile.comparison.value !== null ? {
                    label: `${tile.comparison.label} ${formatValue(tile.comparison.value, measureFormat(tile.comparison.format))}`,
                    tone: tile.comparison.worse === true ? 'warning' : tile.comparison.worse === false ? 'good' : 'neutral',
                    detail: tile.comparison.definition ?? undefined,
                  } : undefined}
                />
              ))}
            </Box>
          )}

          {/* The list comes first when the spec puts it before the rankings
              (a call list is what the page is for); otherwise the first row
              of ranking tables, the chart across the full width, any tables
              left over, then the list. */}
          {/* A grid sits where the spec puts it beside the list: above it, or
              straight after it (the Daily Call Plan's past pickups, 21 Oct
              2026, so today's list comes first). */}
          {gridsBefore.map(gridPanel)}
          {records && recordsFirst && recordsPanel(records)}
          {records && recordsFirst && gridsAfter.map(gridPanel)}
          {renderRankings(rankings.slice(0, rankingColumns))}
          {monthly && <MonthlyPanel block={monthly} loading={loading} />}
          {renderRankings(rankings.slice(rankingColumns))}

          {records && !recordsFirst && recordsPanel(records)}
          {records && !recordsFirst && gridsAfter.map(gridPanel)}
          {gridsLast.map(gridPanel)}

          {payload.rules && payload.rules.length > 0 && <RulesPanel groups={payload.rules} />}
        </>
      )}
    </Box>
  );

  // What the chat's answers are scoped to, shown in its composer: the route
  // and each applied filter, the same scope sent as surface context.
  const chatScope = [
    ...(lookedUp ? [{ key: 'lookup', label: `${lookedUp} · ${countLabel(recordCount, noun)}` }] : []),
    ...(payload.day_picker ? [{ key: 'day', label: `${payload.day_picker.label}: ${payload.day_picker.value}` }] : []),
    ...(payload.zone_picker ? [{ key: 'zone', label: `${payload.zone_picker.label}: ${payload.zone_picker.value}` }] : []),
    ...(range && dates ? [{ key: 'dates', label: `${dates.label}: ${range.start ?? '…'} to ${range.end ?? '…'}` }] : []),
    ...(payload.filters ?? [])
      .filter((f) => f.selected.length > 0)
      .map((f) => ({
        key: f.key,
        label: `${f.label}: ${f.selected[0]}${f.selected.length > 1 ? ` +${f.selected.length - 1}` : ''}`,
      })),
  ];

  return (
    <SidePanelDock
      open={chatOpen}
      storageKey="klaris_board_chat_width"
      label="Resize chat panel"
      panel={
        <BoardChatPanel
          useStore={chatStore}
          boardName={boardName}
          place={chatPlace}
          onSend={sendMessage}
          onStop={stopStream}
          onSQLApproval={sendSQLApproval}
          onClose={() => setChatOpen(false)}
          contextChips={chatScope}
        />
      }
    >
      {board}
    </SidePanelDock>
  );
}
